import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Bug, Sparkles } from 'lucide-react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useApplication } from '../../contexts/ApplicationContext';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { FixBoardLane, FixBoardResponse, FixLane, FixListResponse, FixResponse } from '../../types/fix';
import { getRepositoryShortName } from '../../utils/repository';
import { Button, EmptyState, Spinner } from '../../design-system';
import { SingleSelectDropdown } from '../../components/common/SingleSelectDropdown';
import toast from '../../design-system/toast';
import FixesHeading from './FixesHeading';
import FixLaneColumn from './FixLaneColumn';
import LinkButton from './LinkButton';
import { BOARD_LANES } from './lanes';

const POLL_INTERVAL_MS = 5000;
/**
 * Fixes fetched per lane up front, and per page as a column is scrolled. A lane with
 * hundreds of merged fixes therefore costs one small page on load, not all of them.
 */
const CARDS_PER_LANE = 12;
const LANE_PAGE_SIZE = 12;

/**
 * Column widths are fluid (each track is 1fr, so they share whatever the window gives
 * them), but the *count* is fixed per breakpoint rather than auto-fit: the four lanes are
 * a pipeline read left to right, and an auto-fit grid would wrap them 3 + 1 on a typical
 * laptop, which breaks that reading.
 *
 * From `sm` up the grid takes the height the page has left over, which is what gives the
 * columns a definite height to scroll inside. Its min-height is the floor: on a window too
 * short for even that, the page scrolls rather than squashing the columns to nothing.
 * Below `sm` the lanes stack and the page scrolls normally.
 */
const BOARD_GRID_BASE = 'grid grid-cols-1 sm:grid-cols-2 items-stretch sm:flex-1 sm:min-h-[15rem]';
const BOARD_GRID_CLASS = `${BOARD_GRID_BASE} xl:grid-cols-4`;
const BOARD_GRID_CLASS_5 = `${BOARD_GRID_BASE} xl:grid-cols-5`;
const BOARD_GRID_STYLE: React.CSSProperties = { gap: 'clamp(10px, 1vw, 18px)' };

const emptyLane = (lane: FixLane): FixBoardLane => ({ lane, count: 0, fixes: [] });

/**
 * Every fix for the selected repository, grouped into the stages of the fix pipeline.
 * The repository comes from the route (the sidebar switcher owns that choice); legacy
 * applications holding several repositories can additionally narrow to one of them.
 */
const FixesBoardPage: React.FC = () => {
  const { applicationId = '' } = useParams<{ applicationId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();
  const { currentApplication } = useApplication();
  const api = useAuthenticatedApi();

  const [board, setBoard] = useState<FixBoardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  // Pages scrolled in beyond each lane's first page. The board request keeps the head of
  // every lane fresh as it polls; these older fixes are fetched once and left alone,
  // since a fix deep in a lane's history rarely changes stage.
  const [extraByLane, setExtraByLane] = useState<Partial<Record<FixLane, FixResponse[]>>>({});
  const [loadingLanes, setLoadingLanes] = useState<FixLane[]>([]);
  const lanesInFlight = useRef<Set<FixLane>>(new Set());
  const pagesRequested = useRef<Set<string>>(new Set());

  const repoFilter = searchParams.get('repo') || '';
  // Memoised so the empty fallback doesn't produce a new array on every render.
  const repositories = useMemo(() => currentApplication?.repositories ?? [], [currentApplication]);

  const loadBoard = useCallback(
    async (showLoader = true) => {
      if (!currentOrganization?.id || !applicationId) return;
      try {
        if (showLoader) setIsLoading(true);
        setError(null);
        const params = new URLSearchParams({
          application_id: applicationId,
          per_lane: String(CARDS_PER_LANE),
        });
        if (repoFilter) params.set('source_repo', repoFilter);
        const response = await api.get<FixBoardResponse>(
          `${apiEndpoints.fixes.board(currentOrganization.id)}?${params.toString()}`,
        );
        setBoard(response);
        hasLoadedRef.current = true;
      } catch (err: any) {
        console.error('Failed to load the fixes board:', err);
        setError(err.message || 'Failed to load fixes');
      } finally {
        if (showLoader) setIsLoading(false);
      }
    },
    [api, applicationId, currentOrganization?.id, repoFilter],
  );

  useEffect(() => {
    // Show the spinner on the first load only; filter changes refresh in place.
    void loadBoard(!hasLoadedRef.current);
  }, [loadBoard]);

  // Pages already scrolled in belong to the previous filter's result set.
  useEffect(() => {
    setExtraByLane({});
    lanesInFlight.current.clear();
    pagesRequested.current.clear();
  }, [applicationId, repoFilter]);

  const lanes = useMemo(() => {
    const byLane = new Map((board?.lanes ?? []).map((lane) => [lane.lane, lane]));
    /** A lane's fresh head followed by whatever has been scrolled in, ids deduped. */
    const withExtra = (lane: FixLane): FixBoardLane => {
      const head = byLane.get(lane) ?? emptyLane(lane);
      const extra = extraByLane[lane];
      if (!extra?.length) return head;
      const seen = new Set(head.fixes.map((fix) => fix.id));
      return { ...head, fixes: [...head.fixes, ...extra.filter((fix) => !seen.has(fix.id))] };
    };
    // A failed or abandoned fix gets its own column, but only when there is one to
    // show — most repositories never see it.
    const attention = withExtra('needs_attention');
    return attention.count > 0 ? [...BOARD_LANES.map(withExtra), attention] : BOARD_LANES.map(withExtra);
  }, [board, extraByLane]);

  const loadMoreInLane = useCallback(
    async (lane: FixLane, loaded: number) => {
      if (!currentOrganization?.id || !applicationId) return;
      // The column re-arms its observer on every render, and an observer fires the moment
      // it starts watching a sentinel that is already on screen — so this gets called
      // repeatedly for the same boundary. Refs (not state, which lands too late) keep it
      // to one request in flight per lane and one request per page.
      const pageKey = `${lane}:${loaded}`;
      if (lanesInFlight.current.has(lane) || pagesRequested.current.has(pageKey)) return;
      lanesInFlight.current.add(lane);
      pagesRequested.current.add(pageKey);
      setLoadingLanes((prev) => (prev.includes(lane) ? prev : [...prev, lane]));
      try {
        const params = new URLSearchParams({
          application_id: applicationId,
          lane,
          limit: String(LANE_PAGE_SIZE),
          skip: String(loaded),
          include_diff: 'false',
        });
        if (repoFilter) params.set('source_repo', repoFilter);
        const response = await api.get<FixListResponse>(
          `${apiEndpoints.fixes.list(currentOrganization.id)}?${params.toString()}`,
        );
        const page = response.fixes ?? [];
        if (page.length) {
          setExtraByLane((prev) => {
            const existing = prev[lane] ?? [];
            const seen = new Set(existing.map((fix) => fix.id));
            return { ...prev, [lane]: [...existing, ...page.filter((fix) => !seen.has(fix.id))] };
          });
        }
      } catch (err: any) {
        console.error(`Failed to load more ${lane} fixes:`, err);
        toast.error('Failed to load more fixes');
        // Let the next scroll retry this page rather than stalling the lane for good.
        pagesRequested.current.delete(pageKey);
      } finally {
        lanesInFlight.current.delete(lane);
        setLoadingLanes((prev) => prev.filter((entry) => entry !== lane));
      }
    },
    [api, applicationId, currentOrganization?.id, repoFilter],
  );

  // Keep the board live while the agent is writing fixes or a PR is being opened.
  const hasWorkInFlight = useMemo(() => {
    if (!board) return false;
    const inProgress = board.lanes.find((lane) => lane.lane === 'in_progress');
    if (inProgress && inProgress.count > 0) return true;
    return board.lanes.some((lane) =>
      lane.fixes.some((fix) => fix.pr_creation_pending || fix.pr_update_pending),
    );
  }, [board]);

  useEffect(() => {
    if (!hasWorkInFlight) return;
    const interval = setInterval(() => void loadBoard(false), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [hasWorkInFlight, loadBoard]);

  const setRepoFilter = (repo: string) => {
    const next = new URLSearchParams(searchParams);
    if (repo) {
      next.set('repo', repo);
    } else {
      next.delete('repo');
    }
    setSearchParams(next);
  };

  const repoOptions = useMemo(
    () => [
      { value: '', label: 'All repos' },
      ...repositories.map((repo) => ({
        value: repo.full_name,
        label: getRepositoryShortName(repo.full_name),
      })),
    ],
    [repositories],
  );

  // The board is wider than a reading-width page (four columns side by side) and, from
  // the `sm` breakpoint up, exactly as tall as the viewport: it never scrolls, because
  // each lane trims itself to what fits and sends the rest to its own page.
  const container = (children: React.ReactNode) => (
    <div
      // `overflow-auto` rather than `hidden`: the lanes size themselves so nothing
      // normally overflows, but a very short window can still be scrolled.
      className="min-h-full sm:h-full sm:overflow-auto"
      style={{ background: 'var(--bg-base)', fontFamily: 'var(--font-sans)' }}
    >
      <div
        className="flex flex-col sm:h-full sm:min-h-0"
        style={{
          width: '100%',
          maxWidth: 1520,
          margin: '0 auto',
          padding: 'clamp(20px, 2.2vw, 30px) clamp(16px, 1.8vw, 32px) clamp(20px, 2.2vw, 30px)',
        }}
      >
        {children}
      </div>
    </div>
  );

  if (orgLoading || (isLoading && !board)) {
    return container(
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
        <Spinner size={28} />
      </div>,
    );
  }

  if (error && !board) {
    return container(
      <EmptyState
        icon={<AlertTriangle size={22} />}
        title="Couldn't load fixes"
        description={error}
        action={<Button onClick={() => void loadBoard()}>Try again</Button>}
      />,
    );
  }

  const total = board?.total ?? 0;
  const readyCount = lanes.find((lane) => lane.lane === 'ready')?.count ?? 0;
  // Five columns when a needs-attention lane is present, four otherwise. Both class
  // strings are literal so Tailwind emits them.
  const gridClass = lanes.length > BOARD_LANES.length ? BOARD_GRID_CLASS_5 : BOARD_GRID_CLASS;

  return container(
    <>
      <FixesHeading
        style={{ flex: 'none' }}
        title="Fixes"
        description={
          total === 0
            ? 'Every AI-generated fix for this repository, from first draft to merged PR.'
            : `${total} ${total === 1 ? 'fix' : 'fixes'} for this repository` +
              (readyCount > 0 ? ` · ${readyCount} waiting for a pull request` : '')
        }
        actions={
          repositories.length > 1 ? (
            <SingleSelectDropdown
              options={repoOptions}
              value={repoFilter}
              onChange={setRepoFilter}
              ariaLabel="Filter fixes by repository"
            />
          ) : undefined
        }
      />

      {total === 0 ? (
        <EmptyState
          icon={<Sparkles size={22} />}
          title="No fixes yet"
          description="Pick a finding and let the Kolega agent write the fix — it will show up here, and move across the board as it gets reviewed and merged."
          action={
            <LinkButton
              to={`/repositories/${applicationId}/findings`}
              variant="primary"
              icon={<Bug size={16} />}
              style={{ padding: '10px 16px', fontSize: 13.5 }}
            >
              View findings
            </LinkButton>
          }
          style={{
            background: 'var(--surface-1)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-2xl)',
            padding: '56px 24px',
          }}
        />
      ) : (
        <>
          <div className={gridClass} style={BOARD_GRID_STYLE}>
            {lanes.map((lane) => (
              <FixLaneColumn
                key={lane.lane}
                lane={lane}
                applicationId={applicationId}
                showRepo={repositories.length > 1 && !repoFilter}
                hasMore={lane.fixes.length < lane.count}
                isLoadingMore={loadingLanes.includes(lane.lane)}
                onLoadMore={() => void loadMoreInLane(lane.lane, lane.fixes.length)}
              />
            ))}
          </div>

        </>
      )}
    </>,
  );
};

export default FixesBoardPage;
